import React, { useEffect, useState } from "react";

/**
 * Text-based date / datetime input that always displays dd/mm/yyyy
 * (and dd/mm/yyyy hh:mm when `mode="datetime"`).
 *
 * Why this exists: browsers ignore both the document `lang` attribute
 * and the per-element `lang="ro"` on `<input type="date">` /
 * `datetime-local` when deciding the picker's format — Chrome / Edge
 * pick the OS locale instead. So clients on US-set machines kept
 * seeing mm/dd/yyyy in our create-user / billing / sessions forms
 * despite our HTML hints.
 *
 * Behaviour:
 *   - `value` is the ISO string the parent already uses:
 *       mode="date"     → yyyy-mm-dd       (matches <input type="date">.value)
 *       mode="datetime" → yyyy-mm-ddTHH:mm (matches datetime-local.value)
 *     So callers can swap the native input for this component without
 *     touching the rest of their form state.
 *   - The user sees and types dd/mm/yyyy [hh:mm]. We auto-insert
 *     separators as they type. Pasting "14062026 1830" yields
 *     "14/06/2026 18:30" instantly.
 *   - On blur we validate; if the date/time is invalid we revert to
 *     whatever the parent currently has, so the form never sits in a
 *     broken state.
 *   - When the value is valid the parent's `onChange` fires with the
 *     ISO form, matching the contract of the native input we replace.
 */
export type DateInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "min" | "max"
> & {
  /** "date" = yyyy-mm-dd, "datetime" = yyyy-mm-ddTHH:mm. Default "date". */
  mode?: "date" | "datetime";
  value?: string;
  onChange?: (isoValue: string) => void;
  min?: string;
  max?: string;
};

// ---------- date-only helpers ----------

function isoDateToDisplay(iso: string | undefined | null): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function displayDateToIso(display: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display.trim());
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  if (!isValidYMD(Number(yyyy), Number(mm), Number(dd))) return null;
  return `${yyyy}-${mm}-${dd}`;
}

function autoFormatDate(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

// ---------- datetime helpers ----------

function isoDateTimeToDisplay(iso: string | undefined | null): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
}

function displayDateTimeToIso(display: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/.exec(display.trim());
  if (!m) return null;
  const [, dd, mm, yyyy, hh, mi] = m;
  if (!isValidYMD(Number(yyyy), Number(mm), Number(dd))) return null;
  const H = Number(hh);
  const M = Number(mi);
  if (H > 23 || M > 59) return null;
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function autoFormatDateTime(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 12); // ddmmyyyyHHMM
  let out = digits;
  if (digits.length > 8) {
    out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)} ${digits.slice(8, 10)}`;
    if (digits.length > 10) out += `:${digits.slice(10, 12)}`;
  } else if (digits.length > 4) {
    out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    // Adding a space hint after the year so the next keystroke is hh.
    if (digits.length === 8) out += " ";
  } else if (digits.length > 2) {
    out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return out;
}

// ---------- shared ----------

function isValidYMD(y: number, mo: number, d: number): boolean {
  if (y < 1 || mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  const date = new Date(Date.UTC(y, mo - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === mo - 1 &&
    date.getUTCDate() === d
  );
}

export default function DateInput({
  mode = "date",
  value,
  onChange,
  min,
  max,
  className,
  placeholder,
  ...rest
}: DateInputProps) {
  const isoToDisplay = mode === "datetime" ? isoDateTimeToDisplay : isoDateToDisplay;
  const displayToIso = mode === "datetime" ? displayDateTimeToIso : displayDateToIso;
  const autoFormat = mode === "datetime" ? autoFormatDateTime : autoFormatDate;
  const maxLength = mode === "datetime" ? 16 : 10; // dd/mm/yyyy hh:mm vs dd/mm/yyyy
  const defaultPlaceholder = mode === "datetime" ? "dd/mm/yyyy hh:mm" : "dd/mm/yyyy";

  const [text, setText] = useState<string>(isoToDisplay(value));

  // Keep the display in sync when the parent supplies a new value
  // (e.g. resetting a form). We only overwrite when the parent's ISO
  // value diverges from what we currently show, so the user's typing
  // isn't interrupted mid-edit.
  useEffect(() => {
    const parentDisplay = isoToDisplay(value);
    setText((prev) => (prev === parentDisplay ? prev : parentDisplay));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, mode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = autoFormat(e.target.value);
    setText(next);
    const iso = displayToIso(next);
    if (iso) {
      if (min && iso < min) return;
      if (max && iso > max) return;
      onChange?.(iso);
    } else if (next === "") {
      onChange?.("");
    }
  };

  const handleBlur = () => {
    const iso = displayToIso(text);
    if (!iso && text !== "") {
      setText(isoToDisplay(value));
    }
  };

  // Native picker, opened from the calendar button. The visible field stays
  // text (so the format is always dd/mm/yyyy regardless of OS locale), but
  // users who prefer clicking a calendar get one instead of typing.
  const pickerRef = React.useRef<HTMLInputElement>(null);
  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    // showPicker() is the reliable way in Chrome/Edge/Safari; fall back to
    // focus+click for browsers that don't implement it.
    const anyEl = el as HTMLInputElement & { showPicker?: () => void };
    if (typeof anyEl.showPicker === "function") {
      try {
        anyEl.showPicker();
        return;
      } catch {
        /* not allowed (e.g. not user-activated) — fall through */
      }
    }
    el.focus();
    el.click();
  };

  return (
    <span className="relative inline-flex items-center w-full">
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder || defaultPlaceholder}
        maxLength={maxLength}
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        className={className}
      />
      <button
        type="button"
        onClick={openPicker}
        tabIndex={-1}
        aria-label="Alege data"
        className="absolute right-2 grid place-items-center w-7 h-7 rounded text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>
      {/* Zero-size native control that actually shows the calendar. */}
      <input
        ref={pickerRef}
        type={mode === "datetime" ? "datetime-local" : "date"}
        value={value || ""}
        min={min}
        max={max}
        onChange={(e) => {
          const iso = e.target.value;
          setText(isoToDisplay(iso));
          onChange?.(iso);
        }}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute right-2 w-7 h-7 opacity-0 pointer-events-none"
      />
    </span>
  );
}
